"use client";

import type { ReactNode } from "react";

import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";

export function BlogInitialLoadingGate({ children }: { children: ReactNode }) {
  const { blog } = useAppContext();
  const { isInitialLoading } = useSessionContext();

  if (!isInitialLoading && blog) {
    return <>{children}</>;
  }

  return <BlogInitialLoadingScreen />;
}

function BlogInitialLoadingScreen() {
  const { language } = useLanguage();
  const loadingText =
    language === "cs" ? "Načítá se průvodce realitami." : "Loading blog guide.";

  return (
    <section
      className="blog-root"
      aria-busy="true"
      aria-live="polite"
      aria-label={loadingText}
    >
      <span className="sr-only">{loadingText}</span>

      <div className="blog-header">
        <div className="h-6 w-44 rounded-full bg-hover animate-pulse" />
        <div className="mt-4 h-12 w-full max-w-4xl rounded-2xl bg-hover animate-pulse sm:h-16" />
        <div className="mt-4 space-y-3 w-full max-w-2xl">
          <div className="h-4 w-full rounded-full bg-hover animate-pulse" />
          <div className="h-4 w-[88%] rounded-full bg-hover animate-pulse" />
          <div className="h-4 w-[72%] rounded-full bg-hover animate-pulse" />
        </div>
      </div>

      <div className="blog-grid">
        <BlogLoadingCard featured />
        <BlogLoadingCard />
        <BlogLoadingCard />
      </div>
    </section>
  );
}

function BlogLoadingCard({ featured = false }: { featured?: boolean }) {
  return (
    <article
      className={`blog-card overflow-hidden ${featured ? "blog-card--featured" : ""}`}
      aria-hidden="true"
    >
      <div className="blog-card-body">
        <div className="blog-tags">
          <div className="h-5 w-20 rounded-full bg-hover animate-pulse" />
          <div className="h-5 w-24 rounded-full bg-hover animate-pulse" />
        </div>

        <div className="space-y-3">
          <div
            className={`h-6 rounded-xl bg-hover animate-pulse ${featured ? "w-[92%]" : "w-[84%]"}`}
          />
          <div className="h-6 w-[76%] rounded-xl bg-hover animate-pulse" />
          <div className="h-6 w-[64%] rounded-xl bg-hover animate-pulse" />
        </div>

        <div className="space-y-2 flex-1 pt-2">
          <div className="h-3 rounded-full bg-hover animate-pulse" />
          <div className="h-3 rounded-full bg-hover animate-pulse" />
          <div className="h-3 w-[86%] rounded-full bg-hover animate-pulse" />
          <div className="h-3 w-[72%] rounded-full bg-hover animate-pulse" />
        </div>

        <div className="blog-card-footer">
          <div className="blog-card-meta w-full max-w-[13rem]">
            <div className="size-3.5 rounded-full bg-hover animate-pulse" />
            <div className="h-3 w-24 rounded-full bg-hover animate-pulse" />
            <div className="h-3 w-3 rounded-full bg-hover animate-pulse" />
            <div className="h-3 w-20 rounded-full bg-hover animate-pulse" />
          </div>
          <div className="h-4 w-16 rounded-full bg-hover animate-pulse" />
        </div>
      </div>
    </article>
  );
}
